import React, { useEffect, useMemo, useState } from 'react';
import {
    Table,
    Button,
    Space,
    Select,
    Card,
    Tabs,
    Tag,
    Typography,
    message,
    Modal,
    Form,
    Input,
    InputNumber,
    DatePicker,
    Row,
    Col,
    Statistic,
    Upload,
    Image,
    Spin,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    UploadOutlined,
    DollarOutlined,
    RiseOutlined,
    FallOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { getAttachmentUrl } from '../../utils/urlConfig';
import { getAllocationRate, formatAmount, formatRate } from '../../utils/allocation';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ProjectTransactionPage = () => {
    const [activeTab, setActiveTab] = useState('income');
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [filters, setFilters] = useState({
        department_id: '',
        transaction_type: 'income',
    });
    const [stats, setStats] = useState({
        total_income: 0,
        total_expense: 0,
        net_amount: 0,
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState('income'); // 'income' or 'expense'
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [fileUploading, setFileUploading] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [projectAllocations, setProjectAllocations] = useState([]);
    const [projectBudgetItems, setProjectBudgetItems] = useState([]);
    const [selectedBudgetCategory, setSelectedBudgetCategory] = useState(null);

    useEffect(() => {
        fetchProjects();
        fetchDepartments();
    }, []);

    useEffect(() => {
        fetchTransactions();
        fetchStats();
    }, [filters]);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = {
                department_id: filters.department_id
            };

            const response = await axios.get(`${API_BASE}/project-transactions/stats`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setStats(response.data.stats || {
                total_income: 0,
                total_expense: 0,
                net_amount: 0
            });
        } catch (error) {
            console.error('통계 조회 실패:', error);
        }
    };

    const fetchProjects = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/projects`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProjects(response.data.projects || []);
        } catch (error) {
            console.error('프로젝트 조회 실패:', error);
        }
    };

    const fetchDepartments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/departments`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const filteredDepts = (response.data.departments || [])
                .filter(dept => dept.code !== 'UNASSIGNED');
            setDepartments(filteredDepts);
        } catch (error) {
            console.error('부서 조회 실패:', error);
        }
    };

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {
                ...filters,
                transaction_type: activeTab,
            };

            console.log('Fetching transactions with params:', params);

            const response = await axios.get(`${API_BASE}/project-transactions`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setTransactions(response.data.transactions || []);
        } catch (error) {
            console.error('거래 내역 조회 실패:', error);
            message.error('거래 내역 조회 실패');
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectAllocations = async (projectId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/projects/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const project = response.data;
            setProjectAllocations(project.allocations || []);
        } catch (error) {
            console.error('프로젝트 배정 부서 조회 실패:', error);
            setProjectAllocations([]);
        }
    };

    const fetchProjectBudgetItems = async (projectId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/project-budget/project/${projectId}/items`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const budgetItems = response.data.budget_items || [];
            setProjectBudgetItems(budgetItems);
        } catch (error) {
            console.error('프로젝트 예산 항목 조회 실패:', error);
            setProjectBudgetItems([]);
        }
    };

    const handleProjectChange = (projectId) => {
        setSelectedProjectId(projectId);
        if (projectId) {
            fetchProjectAllocations(projectId);
            fetchProjectBudgetItems(projectId);
        } else {
            setProjectAllocations([]);
            setProjectBudgetItems([]);
        }
        // 귀속부서와 예산항목 초기화
        form.setFieldsValue({
            department_id: undefined,
            project_budget_item_id: undefined
        });
        setSelectedBudgetCategory(null);
    };

    const handleBudgetItemChange = (budgetItemId) => {
        if (budgetItemId) {
            // 선택된 예산 항목 찾기
            const selectedItem = projectBudgetItems.find(item => item.id === budgetItemId);
            if (selectedItem && selectedItem.vendor_name) {
                // vendor_name이 있으면 거래처/업체명 필드에 자동 입력
                form.setFieldsValue({
                    vendor: selectedItem.vendor_name
                });
            }
        } else {
            // 예산 항목이 Clear된 경우 거래처명도 초기화 (선택사항)
            // form.setFieldsValue({
            //     vendor: ''
            // });
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setFilters(prev => ({ ...prev, transaction_type: key }));
    };

    const handleFilterChange = (field, value) => {
        setFilters({ ...filters, [field]: value });
    };

    const handleCreate = (type) => {
        setModalType(type);
        setEditingTransaction(null);
        form.resetFields();
        setFileList([]);
        setFileUploading(false);
        setSelectedProjectId(null);
        setProjectAllocations([]);
        setProjectBudgetItems([]);
        setSelectedBudgetCategory(null);
        
        // 지출 등록 시 기본값 설정
        if (type === 'expense') {
            form.setFieldsValue({
                expense_type: 'invoice' // 계산서로 기본값 설정
            });
            // 예산 카테고리는 기본값 없이 "선택하세요"로 시작
        }
        
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setModalType(record.transaction_type);
        setEditingTransaction(record);
        form.setFieldsValue({
            ...record,
            transaction_date: record.transaction_date ? dayjs(record.transaction_date) : null,
            payment_date: record.payment_date ? dayjs(record.payment_date) : null,  // ⭐ 추가
        });
        setFileList([]);
        setFileUploading(false);
        
        // 편집할 때 프로젝트 정보 로드
        if (record.project_id) {
            setSelectedProjectId(record.project_id);
            fetchProjectAllocations(record.project_id);
            fetchProjectBudgetItems(record.project_id);
        }
        
        // 예산 카테고리 상태 설정
        if (record.budget_category) {
            setSelectedBudgetCategory(record.budget_category);
        } else {
            setSelectedBudgetCategory(null);
        }
        
        setModalVisible(true);
    };

    const handleDelete = (id) => {
        confirm({
            title: '거래 내역을 삭제하시겠습니까?',
            icon: <ExclamationCircleOutlined />,
            content: '삭제된 데이터는 복구할 수 없습니다.',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE}/project-transactions/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    message.success('삭제되었습니다');
                    // 거래내역과 통계를 병렬로 새로고침
                    await Promise.all([
                        fetchTransactions(),
                        fetchStats()
                    ]);
                } catch (error) {
                    message.error('삭제 실패');
                    console.error('Delete error:', error);
                }
            },
        });
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const token = localStorage.getItem('token');

            const formData = new FormData();
            Object.keys(values).forEach(key => {
                if (values[key] !== undefined && values[key] !== null) {
                    if (key === 'transaction_date' || key === 'payment_date') {
                        formData.append(key, values[key].format('YYYY-MM-DD'));
                    } else {
                        formData.append(key, values[key]);
                    }
                }
            });

            // 첨부파일 처리
            if (fileList.length > 0) {
                const file = fileList[0].originFileObj || fileList[0];
                if (file && file instanceof File) {
                    formData.append('attachment', file);
                    console.log('📎 첨부파일 FormData에 추가됨:', file.name, file.size);
                } else {
                    console.log('⚠️ 파일 객체가 유효하지 않음:', file);
                }
            } else {
                console.log('📎 첨부된 파일 없음');
            }

            // FormData 내용 디버깅
            console.log('📤 전송할 FormData (프로젝트 거래):');
            for (let pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(`  ${pair[0]}: File(${pair[1].name}, ${pair[1].size} bytes)`);
                } else {
                    console.log(`  ${pair[0]}: ${pair[1]}`);
                }
            }

            let response;

            // ⭐ 수정 vs 등록 분기
            if (editingTransaction) {
                // 수정
                const endpoint = modalType === 'income'
                    ? `${API_BASE}/project-transactions/income/${editingTransaction.id}`
                    : `${API_BASE}/project-transactions/expense/${editingTransaction.id}`;

                response = await axios.put(endpoint, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                message.success(`${modalType === 'income' ? '수입' : '지출'}이 수정되었습니다`);
            } else {
                // 등록
                const endpoint = modalType === 'income'
                    ? `${API_BASE}/project-transactions/income`
                    : `${API_BASE}/project-transactions/expense`;

                response = await axios.post(endpoint, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                message.success(`${modalType === 'income' ? '수입' : '지출'}이 등록되었습니다`);
            }

            setModalVisible(false);
            // 거래내역과 통계를 병렬로 새로고침
            await Promise.all([
                fetchTransactions(),
                fetchStats()
            ]);
        } catch (error) {
            message.error(error.response?.data?.message || '처리 실패');
            console.error('Submit error:', error);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        fileList,
        beforeUpload: (file) => {
            // 파일 형식 검증
            const isValidFormat = file.type.startsWith('image/') || file.type === 'application/pdf';
            if (!isValidFormat) {
                message.error('JPG, PNG, PDF 형식의 파일만 업로드 가능합니다');
                return false;
            }
            
            // 파일 크기 검증 (10MB)
            const isValidSize = file.size / 1024 / 1024 < 10;
            if (!isValidSize) {
                message.error('파일 크기는 10MB 이하여야 합니다');
                return false;
            }
            
            // 로딩 상태 시작
            setFileUploading(true);
            
            // 파일 처리 시뮬레이션 (실제 처리 시간을 반영)
            setTimeout(() => {
                // 파일 리스트에 추가 (originFileObj 속성 포함)
                setFileList([{
                    uid: file.uid,
                    name: file.name,
                    status: 'done',
                    originFileObj: file
                }]);
                setFileUploading(false);
                console.log('📎 첨부파일 선택됨:', file.name, file.size);
            }, 500); // 0.5초 딜레이
            
            return false;
        },
        onRemove: () => {
            setFileList([]);
        },
    };

    // 부서를 선택한 경우 매출 귀속부서 배분 기준으로 거래 목록을 재구성
    //  - 수입: 프로젝트의 매출 배분율만큼 안분 (배분이 없는 부서는 목록에서 제외)
    //  - 지출: 등록 시 지정된 귀속부서 기준 전액 (미지정이면 대표 귀속부서로 간주)
    const displayTransactions = useMemo(() => {
        const departmentId = filters.department_id;

        if (!departmentId) {
            return transactions.map(t => ({ ...t, amount_allocation_rate: 100, contract_allocation_rate: 100 }));
        }

        return transactions
            .map(t => {
                const contractRate = getAllocationRate(t.project, departmentId);

                if (t.transaction_type === 'expense') {
                    const ownerDepartmentId = t.department_id ?? t.project?.primary_department_id;
                    if (Number(ownerDepartmentId) !== Number(departmentId)) return null;

                    return { ...t, amount_allocation_rate: 100, contract_allocation_rate: contractRate };
                }

                if (contractRate <= 0) return null;

                return { ...t, amount_allocation_rate: contractRate, contract_allocation_rate: contractRate };
            })
            .filter(Boolean);
    }, [transactions, filters.department_id]);

    // 배분율이 100% 미만이면 "전체금액 (배분금액 - 배분율%)" 형태로 표시
    const renderAllocatedAmount = (amount, rate, { strong = false, color } = {}) => {
        const totalAmount = parseFloat(amount) || 0;
        const appliedRate = rate ?? 100;
        const totalText = formatAmount(totalAmount);

        if (appliedRate >= 100) {
            const content = color ? <Text style={{ color }}>{totalText}</Text> : totalText;
            return strong ? <strong>{content}</strong> : content;
        }

        return (
            <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{totalText}</Text>
                <Text strong style={{ color: color || '#1890ff' }}>
                    ({formatAmount(totalAmount * appliedRate / 100)} - {formatRate(appliedRate)})
                </Text>
            </Space>
        );
    };

    // 프로젝트 계약금액 컬럼 (선택된 부서의 매출 배분금액 함께 표시)
    const contractAmountColumn = {
        title: '계약금액',
        key: 'contract_amount',
        width: 175,
        align: 'right',
        render: (_, record) => renderAllocatedAmount(
            record.project?.contract_amount,
            record.contract_allocation_rate,
            { color: '#722ed1' }
        ),
    };

    // 수입 테이블 컬럼
    const incomeColumns = [
        {
            title: '발생일',
            dataIndex: 'transaction_date',
            key: 'transaction_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            width: 200,
            ellipsis: true,
        },
        contractAmountColumn,
        {
            title: '거래처',
            dataIndex: 'vendor',
            key: 'vendor',
            width: 150,
        },
        {
            title: '공급가액',
            dataIndex: 'amount',
            key: 'amount',
            width: 175,
            align: 'right',
            render: (amount, record) => renderAllocatedAmount(amount, record.amount_allocation_rate),
        },
        {
            title: '부가세',
            key: 'tax',
            width: 165,
            align: 'right',
            render: (_, record) => renderAllocatedAmount(
                (parseFloat(record.amount) || 0) * 0.1,
                record.amount_allocation_rate
            ),
        },
        {
            title: '총액',
            key: 'total',
            width: 175,
            align: 'right',
            render: (_, record) => renderAllocatedAmount(
                (parseFloat(record.amount) || 0) * 1.1,
                record.amount_allocation_rate,
                { strong: true }
            ),
        },
        {
            title: '입금/지급일',
            dataIndex: 'payment_date',
            key: 'payment_date',
            width: 120,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '첨부파일',
            dataIndex: 'attachment_url',
            key: 'attachment_url',
            width: 80,
            align: 'center',
            render: (attachment_url) => (
                attachment_url ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Text style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 12 }}>O</Text>
                        <Image
                            width={30}
                            height={30}
                            src={getAttachmentUrl(attachment_url)}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                            style={{ borderRadius: 4 }}
                            preview={{
                                mask: (
                                    <div style={{ fontSize: 10 }}>
                                        <EyeOutlined />
                                    </div>
                                )
                            }}
                        />
                    </div>
                ) : (
                    <Text style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: 14 }}>X</Text>
                )
            ),
        },
        {
            title: '상태',
            key: 'status',
            width: 80,
            render: (_, record) => {
                return record.payment_date ? (
                    <Tag color="success">완료</Tag>
                ) : (
                    <Tag color="warning">대기</Tag>
                );
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        수정
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                        size="small"
                    >
                        삭제
                    </Button>
                </Space>
            ),
        },
    ];

    // 지출 테이블 컬럼
    const expenseColumns = [
        {
            title: '발생일',
            dataIndex: 'transaction_date',
            key: 'transaction_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            width: 180,
            ellipsis: true,
        },
        contractAmountColumn,
        {
            title: '귀속 부서',
            key: 'department',
            width: 130,
            render: (_, record) => {
                // 지출은 등록 시 지정한 귀속부서 기준 (미지정이면 대표 귀속부서)
                const deptName = record.department?.name || record.project?.primary_department?.name;
                return deptName ? <Tag color="blue">{deptName}</Tag> : '-';
            },
        },
        {
            title: '거래처',
            dataIndex: 'vendor',
            key: 'vendor',
            width: 150,
        },
        {
            title: '공급가액',
            dataIndex: 'amount',
            key: 'amount',
            width: 130,
            align: 'right',
            render: (amount) => formatAmount(amount),
        },
        {
            title: '부가세',
            key: 'tax',
            width: 120,
            align: 'right',
            render: (_, record) => formatAmount((parseFloat(record.amount) || 0) * 0.1),
        },
        {
            title: '총액',
            key: 'total',
            width: 130,
            align: 'right',
            render: (_, record) => (
                <strong>{formatAmount((parseFloat(record.amount) || 0) * 1.1)}</strong>
            ),
        },
        {
            title: '입금/지급일',
            dataIndex: 'payment_date',
            key: 'payment_date',
            width: 120,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '첨부파일',
            dataIndex: 'attachment_url',
            key: 'attachment_url',
            width: 80,
            align: 'center',
            render: (attachment_url) => (
                attachment_url ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Text style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 12 }}>O</Text>
                        <Image
                            width={30}
                            height={30}
                            src={getAttachmentUrl(attachment_url)}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                            style={{ borderRadius: 4 }}
                            preview={{
                                mask: (
                                    <div style={{ fontSize: 10 }}>
                                        <EyeOutlined />
                                    </div>
                                )
                            }}
                        />
                    </div>
                ) : (
                    <Text style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: 14 }}>X</Text>
                )
            ),
        },
        {
            title: '상태',
            key: 'status',
            width: 80,
            render: (_, record) => {
                return record.payment_date ? (
                    <Tag color="success">완료</Tag>
                ) : (
                    <Tag color="warning">대기</Tag>
                );
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        수정
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                        size="small"
                    >
                        삭제
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2}>프로젝트 수입/지출 관리</Title>
            </div>

            {/* 통계 카드 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="총 수입"
                            value={stats.total_income}
                            prefix={<RiseOutlined style={{ color: '#52c41a' }} />}
                            suffix="원"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="총 지출"
                            value={stats.total_expense}
                            prefix={<FallOutlined style={{ color: '#ff4d4f' }} />}
                            suffix="원"
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="수익"
                            value={stats.net_amount}
                            prefix={<DollarOutlined />}
                            suffix="원"
                            valueStyle={{ color: stats.net_amount >= 0 ? '#52c41a' : '#ff4d4f' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 필터 */}
            <Card style={{ marginBottom: 24 }}>
                <Space wrap>
                    <Select
                        placeholder="귀속 부서 선택"
                        value={filters.department_id || ''}
                        onChange={(value) => handleFilterChange('department_id', value)}
                        style={{ width: 250 }}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                    >
                        <Select.Option value="">전체 부서</Select.Option>
                        {departments.map(d => (
                            <Select.Option key={d.id} value={d.id}>
                                {d.name} ({d.code})
                            </Select.Option>
                        ))}
                    </Select>
                    {filters.department_id && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            수입은 매출 귀속부서 배분율로 안분, 지출은 귀속부서 기준 전액으로 표시됩니다
                        </Text>
                    )}
                </Space>
            </Card>

            {/* 탭 */}
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    tabBarExtraContent={
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleCreate(activeTab)}
                        >
                            {activeTab === 'income' ? '수입 등록' : '지출 등록'}
                        </Button>
                    }
                    items={[
                        {
                            key: 'income',
                            label: '수입 관리',
                            children: (
                                <Table
                                    columns={incomeColumns}
                                    dataSource={displayTransactions}
                                    rowKey="id"
                                    loading={loading}
                                    scroll={{ x: 1800 }}
                                    pagination={{
                                        pageSize: 10,
                                        showTotal: (total) => `총 ${total}건`,
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'expense',
                            label: '지출 관리',
                            children: (
                                <Table
                                    columns={expenseColumns}
                                    dataSource={displayTransactions}
                                    rowKey="id"
                                    loading={loading}
                                    scroll={{ x: 1750 }}
                                    pagination={{
                                        pageSize: 10,
                                        showTotal: (total) => `총 ${total}건`,
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </Card>

            {/* 등록/수정 모달 */}
            <Modal
                title={`${modalType === 'income' ? '수입' : '지출'} ${editingTransaction ? '수정' : '등록'}`}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={800}
                okText="저장"
                cancelText="취소"
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>

                        <Col span={12}>
                            <Form.Item
                                name="project_id"
                                label="프로젝트"
                                rules={[{ required: true, message: '프로젝트를 선택하세요' }]}
                            >
                                <Select
                                    placeholder="프로젝트 선택"
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={handleProjectChange}
                                    options={projects.map(p => ({
                                        label: `${p.name} (${p.code})`,
                                        value: p.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        
                        {modalType === 'expense' && (
                            <>
                                <Col span={12}>
                                    <Form.Item
                                        name="department_id"
                                        label="귀속 부서"
                                        rules={[{ required: true, message: '귀속 부서를 선택하세요' }]}
                                    >
                                        <Select
                                            placeholder={selectedProjectId ? "매출 귀속 부서 선택" : "먼저 프로젝트를 선택하세요"}
                                            showSearch
                                            optionFilterProp="label"
                                            disabled={!selectedProjectId}
                                            options={projectAllocations.map(allocation => ({
                                                label: `${allocation.department?.name} (${allocation.department?.code}) - ${allocation.allocation_rate}%`,
                                                value: allocation.department?.id,
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="expense_type"
                                        label="지출 유형"
                                        rules={[{ required: true, message: '지출 유형을 선택하세요' }]}
                                    >
                                        <Input
                                            value="계산서"
                                            readOnly
                                            style={{ backgroundColor: '#f5f5f5', color: '#000' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="budget_category"
                                        label="예산 카테고리"
                                        rules={[{ required: true, message: '예산 카테고리를 선택하세요' }]}
                                    >
                                        <Select
                                            placeholder="선택하세요"
                                            onChange={(value) => {
                                                // 카테고리 변경 시 예산항목 초기화 및 상태 업데이트
                                                form.setFieldsValue({ project_budget_item_id: undefined });
                                                setSelectedBudgetCategory(value);
                                            }}
                                            options={[
                                                { label: '외주비', value: 'outsourcing' },
                                                { label: '자재비', value: 'material' },
                                                { label: '경비', value: 'expense' },
                                                { label: '기타', value: 'other' },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="project_budget_item_id"
                                        label="예산 항목"
                                        extra="프로젝트 예산편성에서 등록된 항목 중 선택"
                                    >
                                        <Select
                                            placeholder={!selectedProjectId ? "먼저 프로젝트를 선택하세요" : "예산 항목 선택 (선택사항)"}
                                            showSearch
                                            optionFilterProp="label"
                                            disabled={!selectedProjectId}
                                            allowClear
                                            onChange={handleBudgetItemChange}
                                            filterOption={(input, option) =>
                                                option.label.toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={projectBudgetItems
                                                .filter(item => {
                                                    return !selectedBudgetCategory || item.category === selectedBudgetCategory;
                                                })
                                                .map(item => ({
                                                    label: item.vendor_name 
                                                        ? `${item.vendor_name} - ${item.description}`
                                                        : item.description,
                                                    value: item.id,
                                                }))}
                                        />
                                    </Form.Item>
                                </Col>
                            </>
                        )}

                        <Col span={12}>
                            <Form.Item
                                name="transaction_date"
                                label="거래일"
                                rules={[{ required: true, message: '거래일을 선택하세요' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>

                        {/* ⭐ 입금/지급일 추가 */}
                        <Col span={12}>
                            <Form.Item
                                name="payment_date"
                                label={modalType === 'income' ? '입금일' : '지급일'}
                                extra="입금/지급 완료 시 날짜를 입력하면 상태가 '완료'로 표시됩니다"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="vendor"
                                label="거래처/업체명"
                                rules={[{ required: true, message: '거래처를 입력하세요' }]}
                            >
                                <Input placeholder="거래처명" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label="공급가액 (부가세 별도)"
                                rules={[{ required: true, message: '금액을 입력하세요' }]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                    addonAfter="원"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="invoice_number" label="계산서/영수증 번호">
                                <Input placeholder="계산서 번호" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="payment_method" label="결제 수단">
                                <Select
                                    placeholder="선택"
                                    options={[
                                        { label: '현금', value: 'cash' },
                                        { label: '계좌이체', value: 'bank_transfer' },
                                        { label: '카드', value: 'card' },
                                        { label: '기타', value: 'other' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="내역 설명">
                        <TextArea rows={3} placeholder="상세 내용" />
                    </Form.Item>

                    <Form.Item name="note" label="비고">
                        <TextArea rows={2} placeholder="추가 메모" />
                    </Form.Item>

                    <Form.Item label="첨부파일 (계산서/영수증)">
                        <Upload {...uploadProps} maxCount={1}>
                            <Button 
                                icon={fileUploading ? <Spin size="small" /> : <UploadOutlined />}
                                loading={fileUploading}
                                disabled={fileUploading}
                            >
                                {fileUploading ? '처리 중...' : '파일 선택'}
                            </Button>
                        </Upload>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            JPG, PNG, PDF 형식 (최대 10MB)
                        </Text>
                    </Form.Item>
                </Form>
            </Modal>

        </div>
    );
};

export default ProjectTransactionPage;